import React, { useCallback, useEffect, useRef, useState } from "react";
import { Dismiss20Regular, ChevronDown20Regular } from "@fluentui/react-icons";

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    children?: React.ReactNode;
    width?: string;
    overlayClassName?: string;
    containerClassName?: string;
}

export function Modal({ isOpen, onClose, title, children, width = '100', overlayClassName = '', containerClassName = '' }: ModalProps): JSX.Element | null {

    const [isScrolledToBottom, setIsScrolledToBottom] = useState(true); // Default to true to hide initially
    const contentRef = useRef<HTMLDivElement>(null);

    const handleScroll = useCallback(() => {
        const content = contentRef.current;
        if (content) {
            const distanceFromBottom = content.scrollHeight - content.scrollTop - content.clientHeight;
            const margin = 10;
            setIsScrolledToBottom(distanceFromBottom <= margin);
        }
    }, []);

    const handleResize = useCallback(() => {
        handleScroll();
    }, [handleScroll]);

    const scrollToBottom = () => {
        const content = contentRef.current;
        if (content) {
            content.scrollTop = content.scrollHeight - content.clientHeight;
        }
    }

    const checkIfScrollable = useCallback(() => {
        const content = contentRef.current;
        if (content) {
            const isScrollable = content.scrollHeight > content.clientHeight;
            if (!isScrollable) {
                setIsScrolledToBottom(true);
            } else {
                handleScroll();
            }
        }
    }, [handleScroll]);

    useEffect(() => {
        const content = contentRef.current;

        if (content) {
            content.addEventListener("scroll", handleScroll);
            setTimeout(checkIfScrollable, 0);
        }

        window.addEventListener("resize", handleResize);

        return () => {
            content?.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", handleResize);
        };
    }, [isOpen, children, checkIfScrollable, handleResize, handleScroll]);

    if (!isOpen) return null;

    return (
        <div
            className={`fixed inset-0 z-10 flex items-center justify-center bg-primary-dark bg-opacity-40 h text-base text-text-light dark:text-text-dark w-100 ${overlayClassName}`}
            onClick={onClose}
            role="dialog"
            aria-modal="true"
        >
            <div
                className={`w-${width} shadow-xl sm:max-w-xl md:max-w-xl lg:max-w-xl xl:max-w-3xl p-6 border-2 border-collapse-header-light dark:border-collapse-header-dark bg-primary-light dark:bg-primary-dark rounded-md overflow-hidden relative m-8 ${containerClassName}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center pb-3 sticky top-0 z-10">
                    <h2 className="text-xl font-semibold">{title || ""}</h2>
                    <Dismiss20Regular onClick={onClose} className="cursor-pointer" />
                </div>
                {!isScrolledToBottom && (
                    <div className="absolute bottom-2 inset-x-0 flex justify-center z-10">
                        <ChevronDown20Regular className="w-6 h-6 dark:text-gray-600 animate-bounce" onClick={scrollToBottom} />
                    </div>
                )}
                <div
                    className="py-4 overflow-y-auto max-h-[70vh] mb-4"
                    ref={contentRef}
                >
                    {children}
                </div>
            </div>
        </div>
    );
}

export default Modal;